import { useState, useEffect } from 'react';
import { 
  CheckCircle2, 
  Rocket, 
  Lock, 
  RefreshCw, 
  X,
  FileCheck,
  Search,
  Check,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

export function UserCompletedUseCases() {
  const { session } = useAuth();
  const [useCases, setUseCases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [selectedCase, setSelectedCase] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchCompleted = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = session?.access_token || localStorage.getItem('compass_token');
      const res = await fetch('/api/user/use-cases', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to load use cases');
      const data = await res.json();
      if (data.success) {
        // Filter strictly to completed cases
        const completed = (data.data || []).filter((uc: any) => 
          uc.status === 'PRODUCTION_DEPLOYED' || uc.status === 'CLOSED' || uc.current_step === 15
        );
        setUseCases(completed);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompleted();
  }, [session]);

  const openReadOnly = async (id: string) => {
    try {
      setDetailLoading(true);
      const token = session?.access_token || localStorage.getItem('compass_token');
      const res = await fetch(`/api/user/use-cases/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setSelectedCase(data.data);
      }
    } catch (e: any) {
      alert(e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const filtered = useCases.filter(uc => 
    uc.title?.toLowerCase().includes(search.toLowerCase()) ||
    uc.number?.toLowerCase().includes(search.toLowerCase()) ||
    uc.mitre_technique?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            <span>My Completed Use Cases</span>
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Production-deployed detection rules that have successfully cleared all 15 workflow stages and dual executive authorizations. (Read-Only)
          </p>
        </div>

        <button
          onClick={fetchCompleted}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border bg-card hover:bg-muted text-foreground transition-colors self-start sm:self-auto"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-xs flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Filter completed rules by number, title, MITRE..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-xs rounded-lg border border-border bg-card text-foreground focus:outline-hidden focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {/* TABLE */}
      <div className="rounded-xl border border-border bg-card shadow-xs overflow-hidden">
        {loading ? (
          <div className="p-16 flex flex-col items-center justify-center text-muted-foreground gap-3">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
            <span className="text-xs">Loading completed production rules...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center text-muted-foreground">
            <Rocket className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm font-medium text-foreground">No completed Use Cases yet</p>
            <p className="text-xs mt-1">Rules transition to Completed once both Step 13 (Aravind Final Approval) and Step 14 (Logesh & Heman Production Approval) are authorized and Step 15 Production Deployment is executed.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted/40 text-muted-foreground uppercase text-[10px] tracking-wider border-b border-border">
                <tr>
                  <th className="py-3 px-4 font-semibold">Number</th>
                  <th className="py-3 px-4 font-semibold">Title</th>
                  <th className="py-3 px-4 font-semibold">Status</th>
                  <th className="py-3 px-4 font-semibold">Priority</th>
                  <th className="py-3 px-4 font-semibold">Workspace</th>
                  <th className="py-3 px-4 font-semibold">Author</th>
                  <th className="py-3 px-4 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((uc) => (
                  <tr key={uc.id} className="hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-4 font-mono font-bold text-emerald-500">
                      {uc.number}
                    </td>
                    <td className="py-3 px-4 font-medium text-foreground max-w-xs truncate">
                      {uc.title}
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                        <Check className="h-3 w-3" />
                        <span>PRODUCTION DEPLOYED</span>
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="font-semibold text-foreground">{uc.priority}</span>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">
                      {uc.workspace || 'Pinochle-IND'}
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">
                      {uc.creator}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => openReadOnly(uc.id)}
                        disabled={detailLoading}
                        className="px-3 py-1 text-[11px] font-semibold rounded-md border border-emerald-500/30 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-600 hover:text-white transition-colors disabled:opacity-50"
                      >
                        {detailLoading ? 'Loading...' : 'Read-Only View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* READ ONLY MODAL */}
      {selectedCase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
          <div className="relative w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl border border-border bg-card shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-border bg-emerald-500/10">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white font-bold shadow-md">
                  <Rocket className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-emerald-600">{selectedCase.number}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-600/20 text-emerald-700 dark:text-emerald-300 font-bold">
                      STAGE 15: PRODUCTION DEPLOYED (READ-ONLY)
                    </span>
                  </div>
                  <h3 className="text-sm font-bold text-foreground line-clamp-1">{selectedCase.title}</h3>
                </div>
              </div>
              <button onClick={() => setSelectedCase(null)} className="h-7 w-7 flex items-center justify-center rounded-lg hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6 text-xs custom-scrollbar">
              {/* Dual Executive Approvals Invariant Box */}
              <div className="p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 space-y-3">
                <h4 className="text-xs font-bold text-emerald-600 uppercase flex items-center gap-1.5">
                  <Lock className="h-4 w-4" />
                  <span>Dual Executive Sign-Off Verification</span>
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px]">
                  <div className="p-3 rounded-lg bg-card border border-border">
                    <span className="text-muted-foreground font-medium">Step 13: Final Authorization</span>
                    <p className="font-bold text-foreground mt-1">{selectedCase.final_approved_by || 'Aravind Chakkaravarthy'}</p>
                    <span className="text-[10px] text-emerald-500 font-semibold">✓ Verified & Approved</span>
                  </div>
                  <div className="p-3 rounded-lg bg-card border border-border">
                    <span className="text-muted-foreground font-medium">Step 14: Production Authorization</span>
                    <p className="font-bold text-foreground mt-1">{selectedCase.production_approved_by || 'Logesh & Heman'}</p>
                    <span className="text-[10px] text-emerald-500 font-semibold">✓ Verified & Approved</span>
                  </div>
                </div>
              </div>

              {/* Technical Specifications */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold uppercase tracking-wide">Rule Description & Logic</h4>
                <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed p-3 rounded-lg bg-muted/30 border border-border">
                  {selectedCase.description}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wide">MITRE Technique</h4>
                  <p className="text-muted-foreground mt-1 font-medium">{selectedCase.mitre_technique || 'T1558.003'}</p>
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wide">Telemetry Source</h4>
                  <p className="text-muted-foreground mt-1 font-medium">{selectedCase.log_source || selectedCase.required_logs || 'Windows Event 4769'}</p>
                </div>
              </div>

              {/* Completed Notice */}
              <div className="p-3 rounded-lg bg-muted/40 text-muted-foreground text-[11px] flex items-center gap-2 border border-border">
                <FileCheck className="h-4 w-4 text-emerald-500 shrink-0" />
                <span>This detection use case is active in live SIEM production tenants. Changes require initiating a new version.</span>
              </div>
            </div>

            <div className="flex items-center justify-end p-4 border-t border-border bg-muted/20">
              <button
                onClick={() => setSelectedCase(null)}
                className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-card border border-border hover:bg-muted"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
